import React, { useState, useEffect, useCallback, JSX } from "react";
import * as signalR from "@microsoft/signalr";
import StyledContainer from "./styles/StyledContainer";
import Header from "./components/Header/Header";
import Content from "./components/Content/Content";
import ChatToggleButton from "./components/ChatToggleButton/ChatToggleButton";
import StyledAppContainer from "./styles/StyledAppContainer";
import initialize from "./api/initialize";
import AppContext from "./contexts/AppContext";
import AppConfigurationType from "./types/AppConfigurationType";
import { lighten } from "polished";
import CONFIG from "./config/config";
import StyledClearConversationButton from "./styles/StyledClearConversationButton";
import { resetCachedConversation } from "./api/messagingService";
import clearButtonIcon from "./assets/images/broom.png";

const App = (): JSX.Element => {
  const [chatOpen, setChatOpen] = useState(false);
  const [configuration, setConfiguration] =
    useState<AppConfigurationType | null>(null);
  const [websocketConnection, setWebsocketConnection] =
    useState<signalR.HubConnection | null>(null);

  const getInitParams = () => {
    const url = new URL(document.location.href);
    const searchParams = url.searchParams;
    const appId = searchParams.get("appId");
    const publicKey = searchParams.get("publicKey");
    const parent = searchParams.get("parent");
    const fullWidth = searchParams.get("fullWidth");

    return [appId, publicKey, fullWidth, parent];
  };

  const [appId, publicKey, fullWidth, parent] = getInitParams();

  const isShowingBubble = () => {
    if (chatOpen) return false;
    if (!configuration) return false;
    if (!configuration.translated_labels) return false;
    if (
      !!window.sessionStorage.getItem(
        `dcinnovationChatBotStopShowingBubble_${appId}`
      )
    )
      return false;

    return !!configuration.translated_labels.bubble_text;
  };

  const initializeWebsocketConnection = async () => {
    const headers = new Headers();
    headers.append("Accept", "application/json");

    if (publicKey) {
      headers.append("Ocp-Apim-Subscription-Key", publicKey);
    }

    const requestOptions: RequestInit = {
      method: "GET",
      headers: headers,
    };

    const response = await fetch(
      CONFIG.domain_url + "message/negotiate_signalR",
      requestOptions
    );
    const data = await response.json();

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(data.url, {
        accessTokenFactory: () => data.accessToken,
      })
      .withAutomaticReconnect()
      .build();

    return connection;
  };

  const reloadApp = async () => {
    if (!appId) return;
    if (!publicKey) return;

    if (websocketConnection) {
      websocketConnection.stop();
    }

    resetCachedConversation(appId);
    window.location.reload();
  };

  const init = useCallback(async () => {
    if (!appId) return;
    if (!publicKey) return;
    if (configuration) return;
    if (websocketConnection) return;

    const data = await initialize({ appId: appId, publicKey: publicKey });
    const connection = await initializeWebsocketConnection();
    await connection.start();

    setWebsocketConnection(connection);
    setConfiguration(data);
  }, [configuration, websocketConnection]);

  const setupBodyBackground = () => {
    if (!configuration) return;

    const bodyBackgroundImage = lighten(0.05, configuration.theme_color);
    const bodyBackgroundImageLighten = lighten(0.2, configuration.theme_color);
    document.body.style.backgroundImage = `linear-gradient(0deg, ${bodyBackgroundImage}, ${bodyBackgroundImageLighten})`;
  };

  const toggleChat = (event: { data: string }) => {
    if (event.data != "toggle_chat") return;

    setChatOpen((chatOpenPrev) => !chatOpenPrev);
  };

  const onCloseBubble = () => {
    if (!configuration) return;
    if (!parent) return;

    window.parent.postMessage(
      {
        chat_open: chatOpen,
        size: configuration.size,
        is_showing_bubble: false,
      },
      parent
    );
  };

  useEffect(() => {
    if (fullWidth) {
      setupBodyBackground();
    }

    if (!fullWidth && parent && configuration) {
      // Update the iframe dimentions based on the setting small | medium | large.
      window.parent.postMessage(
        {
          chat_open: chatOpen,
          size: configuration.size,
          is_showing_bubble: isShowingBubble(),
        },
        parent
      );
    }
  }, [configuration, chatOpen]);

  useEffect(() => {
    init();

    if (fullWidth) {
      setChatOpen(true);
    }

    window.addEventListener("message", toggleChat);
    return () => window.removeEventListener("message", toggleChat);
  }, []);

  if (!configuration) return <></>;
  if (!websocketConnection) return <></>;
  if (!appId) return <></>;
  if (!publicKey) return <></>;

  return (
    <AppContext.Provider
      value={{
        appId: appId,
        publicKey: publicKey,
        configuration: configuration,
        websocketConnection: websocketConnection,
        setWebsocketConnection: setWebsocketConnection,
      }}
    >
      <StyledAppContainer $fullWidth={fullWidth}>
        {chatOpen && (
          <StyledContainer>
            <Header />
            <Content />
          </StyledContainer>
        )}
        {!fullWidth && (
          <ChatToggleButton
            chatOpen={chatOpen}
            setChatOpen={setChatOpen}
            onCloseBubble={onCloseBubble}
          />
        )}

        {fullWidth && (
          <StyledClearConversationButton onClick={reloadApp}>
            <img src={clearButtonIcon} height="20" width="20" alt="clear" />
          </StyledClearConversationButton>
        )}
      </StyledAppContainer>
    </AppContext.Provider>
  );
};

export default App;
